/**
 * BasePhysicsEngine - reusable logic for multiple physics backends. Manages physics
 * bodies associated with scene components and dispatches collision events. Defines
 * abstract `do*` methods for concrete engines to implement.
 */

import * as THREE from 'three';

import { CSGComponent } from '../components/csg/CSGComponent.js';
import { PrimitiveComponent } from '../components/PrimitiveComponent.js';
import { InstancedMeshComponent } from '../components/visual/InstancedMeshComponent.js';
import { InstancedModelMeshComponent } from '../components/visual/InstancedModelMeshComponent.js';
import { MeshComponent } from '../components/visual/MeshComponent.js';
import { ModelMeshComponent } from '../components/visual/ModelMeshComponent.js';
import { TerrainMeshComponent } from '../components/visual/TerrainMeshComponent.js';
import { VoxelMeshComponent } from '../components/voxel/VoxelMeshComponent.js';
import { CollisionConfig } from '../systems/collision/CollisionConfig.js';
import { CollisionMode } from '../systems/collision/CollisionTypes.js';
import { ConsoleCommand } from '../systems/ConsoleManager.js';
import { ensure } from '../utils/Utils.js';


import { CollisionEventType } from './PhysicsEngine.js';
import { CollisionMeshType, PhysicsMotionType } from './PhysicsTypes.js';

import type { IPhysicsVehicle } from './IPhysicsVehicle.js';
import type { PhysicsBodyDesc } from './PhysicsEngine.js';
import type {
  CharacterControllerOptions,
  CollisionEvent,
  HitTestOptions,
  HitTestResult,
  IPhysicsEngine,
  PhysicsOptions,
  PhysicsScalarParam,
  PhysicsVectorParam,
  PrimitiveComponentPhysicsState,
  RawCollisionEvent,
  VectorArray
} from './PhysicsEngine.js';
import type { ComponentPhysicsOptions, HeightfieldCollisionDesc, VoxelCollisionDesc } from './PhysicsTypes.js';
import type { BasePawnMovementComponent } from '../components/movement/BasePawnMovementComponent.js';
import type { SceneComponent } from '../components/SceneComponent.js';


export type BaseDesc = {
    motionType: PhysicsMotionType,
    options: ComponentPhysicsOptions,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    mass: number,
}

export type BoxBodyDesc = BaseDesc & {
    size: THREE.Vector3,
}

export type MeshBodyDesc = BaseDesc & {
    meshes: THREE.Mesh[],
}

export type VoxelBodyDesc = BaseDesc & {
  voxelSize: THREE.Vector3,
  offset?: THREE.Vector3,
  voxels: Float32Array | Int32Array
}

export type SphereBodyDesc = BaseDesc & {
    center: THREE.Vector3,
    radius: number,
}

export type CylinderBodyDesc = BaseDesc & {
    height: number,
    radius: number,
}

export type CapsuleBodyDesc = BaseDesc & {
    height: number,
    radius: number,
}

export type ConeBodyDesc = BaseDesc & {
    height: number,
    radius: number,
}

export type HeightFieldBodyDesc = BaseDesc & {
    width: number,
    depth: number,
    widthPixels: number,
    depthPixels: number,
    heights: Float32Array,
    /**
     * Optional rotation correction applied to the collider to match visual geometry orientation.
     * Applied after the component's world rotation.
     */
    colliderRotationCorrection?: THREE.Quaternion,
}

export enum JointType {
    Spherical = 'spherical',
    Fixed = 'fixed',
    Revolute = 'revolute',
    Prismatic = 'prismatic'
}

export interface JointDesc {
    type: JointType;
    componentA: PrimitiveComponent;
    componentB: PrimitiveComponent;
    anchorA: THREE.Vector3;
    anchorB: THREE.Vector3;
    axis?: THREE.Vector3; // For revolute/prismatic joints
}

export abstract class BasePhysicsEngine<TRecord, TCharacterControllerRecord, TJointRecord = any> implements IPhysicsEngine {
  protected initialized: boolean = false;
  protected bodies: Map<PrimitiveComponent, TRecord[]> = new Map();
  protected characterControllers: Map<BasePawnMovementComponent, TCharacterControllerRecord> = new Map();
  protected joints: Map<string, TJointRecord> = new Map(); // Using string ID as key
  protected directBodies: Map<string, TRecord> = new Map(); // Direct physics bodies by handle
  protected options: PhysicsOptions | null = null;
  private nextJointId: number = 0;
  private nextBodyId: number = 0;

  // Reference to the game world that owns this physics engine
  protected gameWorld: any;

  protected physicMeshesCache: Map<number, THREE.Mesh[]> = new Map();

  constructor(gameWorld?: any) {
    this.gameWorld = gameWorld;
    if (gameWorld) {
      gameWorld?.consoleManager.addCommandHandler(this);
    }
  }

  public performHitTest(options: HitTestOptions): HitTestResult[] {
    return []; // Override in derived class
  }

  @ConsoleCommand('debugphysics', 'Enable/disable physics debug visualization (usage: debugphysics <true | false>)')
  private debugPhysics(enable: string = ''): string {
    // Use truthy value to enable debug visualization
    if (enable === 'true' || enable === '1') {
      this.setDebugRendererEnabled(true);
    } else {
      this.setDebugRendererEnabled(false);
    }
    return 'Debug physics ' + (this.isDebugRendererEnabled() ? 'enabled' : 'disabled');
  }


  /**
     * Initializes the physics engine with the specified options.
     * @param options - Physics engine configuration options
     * @returns Promise that resolves to true if initialization was successful
     */
  public async create(options: PhysicsOptions): Promise<boolean> {
    if (!ensure(!this.initialized)) {
      return false;
    }
    this.initialized = await this.doCreate(options);
    this.options = options;
    return this.initialized;
  }

  /**
     * Get the physics engine options.
     * @returns Physics engine options
     */
  public getOptions(): PhysicsOptions | null {
    return this.options;
  }

  /**
     * Destroys the physics engine and cleans up resources.
     */
  public destroy(): void {
    if (!this.initialized) {
      return;
    }

    // Clean up all joints before destroying the physics world
    for (const [jointId, jointRecord] of this.joints) {
      this.doRemoveJoint(jointRecord);
    }
    this.joints.clear();

    // Clean up all direct physics bodies
    for (const [handle, record] of this.directBodies) {
      this.doRemoveDirectBody(record);
    }
    this.directBodies.clear();

    if (this.doDestroy()) {
      this.initialized = false;
    }

    if (this.gameWorld) {
      this.gameWorld?.consoleManager.removeCommandHandler(this);
    }
  }

  /**
     * Updates the physics simulation by one time step.
     * @param deltaTime - Time elapsed since last tick in seconds
     */
  @THREE.profile
  public tick(deltaTime: number): void {
    if (!this.initialized) {
      return;
    }

    // update component transform to the physics just before ticking
    THREE.ProfilerService.begin('component.syncToPhysics');
    for (const component of this.bodies.keys()) {
      component.syncToPhysics(this);
    }
    THREE.ProfilerService.end('component.syncToPhysics');

    this.doTick(deltaTime);
    this.processCollisionEvents();
  }

  private cloneMesh(originalMesh: THREE.Mesh): THREE.Mesh {
    const clonedMesh = new THREE.Mesh(
      originalMesh.geometry.clone(),
      originalMesh.material
    );
    return clonedMesh;
  }

  /**
   * Processes a GLTF model to extract physics meshes with proper scaling and transforms.
   * @param model - The GLTF scene model
   * @returns Array of processed physics meshes. The meshes are always meshes at Identity, i.e. sitting at origin, no rotation, no scale
   */
  private processGLTFModelForPhysics(model: THREE.Object3D): THREE.Mesh[] {
    // Collect all physics meshes
    const physicsMeshes: THREE.Mesh[] = [];

    // we're now using the model template, so that means it is sitting at Identity, and we don't need to do any transforms to it.
    // of course, while this saves the copy, we are expecting they NEVER get modified
    model.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        physicsMeshes.push(object);
      }
    });

    return physicsMeshes;

    // // alternative way is to clone all meshes, and apply the local to root transform to each mesh
    // const worldToRootMatrix = model.matrixWorld.clone().invert();

    // // Process all meshes in the cloned model
    // model.traverse((object) => {
    //   if (object instanceof THREE.Mesh) {
    //     const physicsMesh = this.cloneMesh(object);

    //     physicsMesh.matrixAutoUpdate = false;
    //     // object local to world -> object local to root -> physics mesh is placed at Identity, so an extra `premultiply(Identity);` is omitted
    //     const matrix = object.matrixWorld.clone().premultiply(worldToRootMatrix);
    //     physicsMesh.matrix.copy(matrix);
    //     physicsMesh.updateMatrixWorld(true);

    //     physicsMeshes.push(physicsMesh);
    //   }
    // });

    // // caching in this.physicMeshesCache

    // return physicsMeshes;
  }

  /**
   * Processes a model to extract physics mesh with proper scaling and transforms.
   * @param mesh - The scene mesh
   * @returns Array of processed physics meshes. The meshes are always meshes at Identity, i.e. sitting at origin, no rotation, no scale
   */
  private processModelForPhysics(mesh: THREE.Mesh): THREE.Mesh {
    const cacheKey = mesh.id;
    if (this.physicMeshesCache.has(cacheKey)) {
      return this.physicMeshesCache.get(cacheKey)![0];
    }

    const physicsMesh = this.cloneMesh(mesh);
    physicsMesh.matrixAutoUpdate = false;
    physicsMesh.matrix.copy(new THREE.Matrix4());
    physicsMesh.updateMatrixWorld(true);

    // cache it so we have the same physicsMesh always, which is good for cache of collider desc
    this.physicMeshesCache.set(cacheKey, [physicsMesh]);

    return physicsMesh;
  }

  /**
     * Adds a scene component to the physics simulation
     * @param component The scene component to add
     * @returns The motion type assigned to the component, or null if addition failed
     */
  public addComponent(component: PrimitiveComponent): PhysicsMotionType | null {
    if (!ensure(this.initialized)) {
      return null;
    }

    if (!ensure(!this.bodies.has(component))) {
      return null;
    }

    const physicsOptions = component.getPhysicsOptions();

    const collisionConfig = CollisionConfig.getInstance();
    const collisionProfile = collisionConfig.profiles.find(profile => profile.name === physicsOptions?.collisionProfile);
    if (collisionProfile?.mode === CollisionMode.NoCollision) {
      return null;
    }

    // FIXME: deprecate? Or should it be a combination of enabled and collisionProfile?
    if (!physicsOptions?.enabled) {
      return null;
    }

    let motionType: PhysicsMotionType | null = null;
    const worldPos = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    const worldScale = new THREE.Vector3();
    component.getWorldPosition(worldPos);
    component.getWorldQuaternion(worldQuat);
    component.getWorldScale(worldScale);

    let records: TRecord[] = [];
    let record: TRecord | null = null;
    if (component instanceof(MeshComponent)) {
      [record, motionType] = this.createMeshComponentRecord(component, physicsOptions, worldPos, worldQuat, worldScale);
    } else if (component instanceof(TerrainMeshComponent)) {
      [record, motionType] = this.createTerrainMeshRecord(component, physicsOptions, worldPos, worldQuat, worldScale);
    } else if (component instanceof(ModelMeshComponent)) {
      [record, motionType] = this.createGLTFMeshRecord(component, physicsOptions, worldPos, worldQuat, worldScale);
    } else if (component instanceof(CSGComponent)) {
      [record, motionType] = this.createCSGComponentRecord(component, physicsOptions, worldPos, worldQuat, worldScale);
    } else if (component instanceof(VoxelMeshComponent)) {
      [record, motionType] = this.createVoxelMeshComponentRecord(component, physicsOptions, worldPos, worldQuat, worldScale);
    }

    if (record) {
      records.push(record);
    }

    if (component instanceof(InstancedModelMeshComponent)) {
      [records, motionType] = this.createInstancedGltfMeshRecord(component, physicsOptions, worldPos, worldQuat);
    } else if (component instanceof(InstancedMeshComponent)) {
      [records, motionType] = this.createInstancedMeshRecord(component, physicsOptions, worldPos, worldQuat, worldScale);
    }

    if (records.length > 0) {
      this.bodies.set(component, records);
    }
    return motionType;
  }

  /**
   * Creates a physics record for a MeshComponent.
   * @param component - The mesh component
   * @param physicsOptions - The physics options
   * @param worldPos - The world position
   * @param worldQuat - The world quaternion
   * @param worldScale - The world scale
   * @returns [record, motionType] tuple
   */
  protected createMeshComponentRecord(
    component: MeshComponent,
    physicsOptions: ComponentPhysicsOptions | undefined,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    worldScale: THREE.Vector3,
  ): [TRecord | null, PhysicsMotionType | null] {
    const mesh = component.mesh;
    const [records, motionType] = this.createMeshRecord(mesh, physicsOptions, [{worldPos, worldQuat, worldScale}], component);
    return [records.length > 0 ? records[0] : null, motionType];
  }

  /**
   * Creates a physics record for a CSGComponent using its result mesh.
   *
   * CSG result meshes have their geometry replaced in-place when reprocessed,
   * so we clone the geometry without caching to avoid stale collider data.
   */
  protected createCSGComponentRecord(
    component: CSGComponent,
    physicsOptions: ComponentPhysicsOptions | undefined,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    worldScale: THREE.Vector3,
  ): [TRecord | null, PhysicsMotionType | null] {
    const resultMesh = component.getResultMesh();
    if (!resultMesh) {
      return [null, null];
    }

    // Clone geometry so physics data is independent of the live mesh.
    // We intentionally bypass processModelForPhysics here because CSG result
    // meshes have their geometry swapped in-place on every CSG rebuild, which
    // would corrupt any cached physics mesh.
    const physicsMesh = new THREE.Mesh(resultMesh.geometry.clone(), resultMesh.material);
    physicsMesh.matrixAutoUpdate = false;
    physicsMesh.matrix.copy(new THREE.Matrix4());
    physicsMesh.updateMatrixWorld(true);

    const motionType = physicsOptions?.motionType ?? PhysicsMotionType.Dynamic;

    const records = this.doCreateMeshBodiesWithTransformOverrides({
      motionType,
      options: physicsOptions!,
      worldPos: new THREE.Vector3(),
      worldQuat: new THREE.Quaternion(),
      mass: this.calculateMeshMass(physicsMesh.geometry, physicsOptions?.density),
      meshes: [physicsMesh],
    }, [{ position: worldPos, rotation: worldQuat, scale: worldScale }]);

    return [records.length > 0 ? records[0] : null, motionType];
  }

  /**
   * Creates a physics record for a VoxelMeshComponent using its built mesh.
   *
   * Voxel meshes have their geometry replaced in-place on every buildMesh call,
   * so we clone the geometry without caching to avoid stale collider data.
   *
   * When `collisionMeshType` is `Voxels` and no explicit `collisionData.voxels`
   * is provided, the voxel coordinates are automatically derived from the
   * component's internal voxel state.
   */
  protected createVoxelMeshComponentRecord(
    component: VoxelMeshComponent,
    physicsOptions: ComponentPhysicsOptions | undefined,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    worldScale: THREE.Vector3,
  ): [TRecord | null, PhysicsMotionType | null] {
    const mesh = component.getMesh();
    if (!mesh.geometry.getAttribute('position')) {
      return [null, null];
    }

    const isVoxelsCollision = physicsOptions?.collisionMeshType === CollisionMeshType.Voxels;

    if (isVoxelsCollision) {
      const motionType = physicsOptions?.motionType ?? PhysicsMotionType.Dynamic;

      // Use explicitly provided voxel data if present; otherwise derive from state.
      let voxelDesc = physicsOptions?.collisionData?.voxels;
      if (!voxelDesc) {
        const state = component.getVoxelState();
        if (state) {
          const filled: number[] = [];
          for (let z = 0; z < state.sizeZ; z++) {
            for (let y = 0; y < state.sizeY; y++) {
              for (let x = 0; x < state.sizeX; x++) {
                const idx = x + state.sizeX * (y + state.sizeY * z);
                if (state.voxels[idx] !== 0) {
                  filled.push(x, y, z);
                }
              }
            }
          }
          voxelDesc = {
            voxelSize: new THREE.Vector3(state.voxelSize, state.voxelSize, state.voxelSize),
            coordinates: new Int32Array(filled),
          };
        }
      }

      if (!voxelDesc || !voxelDesc.coordinates || voxelDesc.coordinates.length === 0) {
        return [null, null];
      }

      const voxelSize = voxelDesc.voxelSize ?? new THREE.Vector3(1, 1, 1);
      const voxels = voxelDesc.coordinates;
      const voxelCount = voxels.length / 3;
      const mass = this.calculateBoxMass(voxelSize, physicsOptions?.density) * voxelCount;

      const record = this.doCreateVoxelBody({
        motionType,
        options: physicsOptions!,
        worldPos,
        worldQuat,
        mass,
        voxels,
        voxelSize,
        offset: voxelDesc.offset,
      });

      return [record, motionType];
    }

    // Non-voxel collision: clone geometry so physics data is independent of the live mesh.
    const physicsMesh = new THREE.Mesh(mesh.geometry.clone(), mesh.material);
    physicsMesh.matrixAutoUpdate = false;
    physicsMesh.matrix.copy(new THREE.Matrix4());
    physicsMesh.updateMatrixWorld(true);

    const motionType = physicsOptions?.motionType ?? PhysicsMotionType.Dynamic;

    const [records, ] = this.createMeshRecord(physicsMesh, physicsOptions, [{ worldPos, worldQuat, worldScale }], component);
    const record = records.length > 0 ? records[0] : null;
    return [record, motionType];
  }

  /**
   * @param mesh - The mesh to create record for
   * @param physicsOptions - The physics options
   * @param worldTransforms - Array of world position, rotation, scale for each instance.
   * Note: for voxels, it creates the voxels collision using the collisionData.voxels per transform,
   * so be aware you likely only need to pass the component's transform
   * @param context - Optional context object for logging
   * @returns [records, motionType] tuple
   */
  private createMeshRecord(
    mesh: THREE.Mesh,
    physicsOptions: ComponentPhysicsOptions | undefined,
    worldTransforms: {worldPos: THREE.Vector3, worldQuat: THREE.Quaternion, worldScale: THREE.Vector3}[],
    context?: any
  ): [(TRecord | null)[], PhysicsMotionType | null] {
    const records: (TRecord | null)[] = [];
    const motionType = physicsOptions?.motionType ?? PhysicsMotionType.Dynamic;

    const geometry = mesh.geometry;
    const heightfieldCollisionDesc = physicsOptions?.collisionData?.heightfield;
    if (heightfieldCollisionDesc) {
      if (motionType !== PhysicsMotionType.Static) {
        console.warn('Heightfield collision only supports static motion. Overriding to static.');
      }
      if (worldTransforms.length > 1) {
        console.warn('Multiple transforms provided for heightfield collision; each transform will create a full heightfield body.');
      }
      for (const {worldPos, worldQuat, worldScale} of worldTransforms) {
        const record = this.createHeightfieldRecord(heightfieldCollisionDesc, physicsOptions!, worldPos, worldQuat, worldScale);
        records.push(record);
      }
      return [records, PhysicsMotionType.Static];
    }

    if (geometry instanceof(THREE.SphereGeometry) || geometry instanceof(THREE.DodecahedronGeometry) || geometry instanceof(THREE.IcosahedronGeometry)) {
      // sphere
      geometry.computeBoundingSphere();
      for (const {worldPos, worldQuat, worldScale} of worldTransforms) {
      // Apply world scale to radius
        const scaleFactor = Math.max(worldScale.x, worldScale.y, worldScale.z);
        const scaledRadius = geometry.boundingSphere!.radius * scaleFactor;
        const record = this.doCreateSphereBody({
          motionType,
          options: physicsOptions!,
          worldPos,
          worldQuat,
          mass: this.calculateSphereMass(scaledRadius, physicsOptions?.density),
          center: geometry.boundingSphere!.center,
          radius: scaledRadius,
        });
        records.push(record);
      }
    } else if (geometry instanceof(THREE.CapsuleGeometry)) {
      const capsuleParams = (geometry as THREE.CapsuleGeometry).parameters;
      for (const {worldPos, worldQuat, worldScale} of worldTransforms) {
      // Apply world scale
        const height = capsuleParams.height * worldScale.y;
        const radius = capsuleParams.radius * Math.max(worldScale.x, worldScale.z);
        const record = this.doCreateCapsuleBody({
          motionType,
          options: physicsOptions!,
          worldPos,
          worldQuat,
          mass: this.calculateCapsuleMass(height, radius, physicsOptions?.density),
          height,
          radius,
        });
        records.push(record);
      }
    } else if (geometry instanceof(THREE.ConeGeometry)) {
      const coneParams = (geometry as THREE.ConeGeometry).parameters;
      for (const {worldPos, worldQuat, worldScale} of worldTransforms) {
      // Apply world scale
        const height = coneParams.height * worldScale.y;
        const radius = coneParams.radius * Math.max(worldScale.x, worldScale.z);
        const record = this.doCreateConeBody({
          motionType,
          options: physicsOptions!,
          worldPos,
          worldQuat,
          mass: this.calculateConeMass(height, radius, physicsOptions?.density),
          height,
          radius,
        });
        records.push(record);
      }
    } else if (geometry instanceof(THREE.CylinderGeometry)) {
      const cylinderParams = (geometry as THREE.CylinderGeometry).parameters;
      for (const {worldPos, worldQuat, worldScale} of worldTransforms) {
      // Apply world scale
        const height = cylinderParams.height * worldScale.y;
        const radius = (cylinderParams.radiusTop + cylinderParams.radiusBottom) / 2 * Math.max(worldScale.x, worldScale.z);
        const record = this.doCreateCylinderBody({
          motionType,
          options: physicsOptions!,
          worldPos,
          worldQuat,
          mass: this.calculateCylinderMass(height, radius, physicsOptions?.density),
          height,
          radius,
        });
        records.push(record);
      }
    } else if (physicsOptions?.collisionMeshType) {
      // Use mesh collider for arbitrary geometry when collisionMeshType is specified
      // console.log('Using mesh collider for', context, 'with type', physicsOptions.collisionMeshType);

      const isVoxelsCollision = physicsOptions.collisionMeshType === CollisionMeshType.Voxels;
      if (isVoxelsCollision) {
        const voxelsCollisionDesc: VoxelCollisionDesc | undefined = physicsOptions.collisionData?.voxels;
        if (!voxelsCollisionDesc) {
          console.log('No voxel collision data provided, using default single voxel at origin. Either provide collisionData.voxels in constructor, or use `overridePhysicsOptions`/`replacePhysicsOptions` to set it later.');
        }

        if (worldTransforms.length > 1) {
          console.warn('Multiple transforms provided for voxel mesh collision, each transform will create a full voxel body based on `collisionData.voxels`.');
        }

        for (const {worldPos, worldQuat, worldScale} of worldTransforms) {
          const voxelSize = voxelsCollisionDesc?.voxelSize ?? new THREE.Vector3(1, 1, 1);
          const voxels: Float32Array | Int32Array = voxelsCollisionDesc?.coordinates ?? new Int32Array([0, 0, 0]);
          const voxelCount = voxels.length / 3;
          const mass = this.calculateBoxMass(voxelSize, physicsOptions?.density) * voxelCount;
          const record = this.doCreateVoxelBody({
            motionType,
            options: physicsOptions!,
            worldPos,
            worldQuat,
            mass,
            voxels,
            voxelSize,
            offset: voxelsCollisionDesc?.offset,
          });
          records.push(record);
        }
      }
      else  {
        // Non-Voxel, build the collision from the mesh geometry
        const physicsMesh = this.processModelForPhysics(mesh);
        const transforms = worldTransforms.map(t => ({position: t.worldPos, rotation: t.worldQuat, scale: t.worldScale}));

        // Now create the physics body at the component's world position/rotation
        records.push(...this.doCreateMeshBodiesWithTransformOverrides({
          motionType,
          options: physicsOptions!,
          worldPos: new THREE.Vector3(), // transforms will provide the position/rotation overrides
          worldQuat: new THREE.Quaternion(),  // same as above
          mass: this.calculateMeshMass(physicsMesh.geometry, physicsOptions?.density),
          meshes: [physicsMesh],
        }, transforms));
      }
    } else {
      // box
      for (const {worldPos, worldQuat, worldScale} of worldTransforms) {
        const size = new THREE.Vector3();
        mesh.geometry.computeBoundingBox();
        mesh.geometry.boundingBox!.getSize(size);
        // Apply world scale to size
        size.multiply(worldScale);
        const record = this.doCreateBoxBody({
          motionType,
          options: physicsOptions!,
          worldPos,
          worldQuat,
          mass: this.calculateBoxMass(size, physicsOptions?.density),
          size,
        });
        records.push(record);
      }
    }

    return [records, motionType];
  }

  private createHeightfieldRecord(
    desc: HeightfieldCollisionDesc,
    options: ComponentPhysicsOptions,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    worldScale: THREE.Vector3
  ): TRecord | null {
    if (desc.widthPixels < 2 || desc.depthPixels < 2) {
      console.error('Heightfield requires at least 2 samples per axis.');
      return null;
    }
    if (desc.width <= 0 || desc.depth <= 0) {
      console.error('Heightfield width/depth must be greater than zero.');
      return null;
    }
    const numPixels = desc.widthPixels * desc.depthPixels;
    if (desc.heights.length !== numPixels) {
      console.error('Heightfield sample count does not match widthPixels*depthPixels.');
      return null;
    }
    const encoding = desc.heightEncoding ?? 'worldY';
    if (encoding === 'normalizedCentered' && !(typeof desc.maxHeight === 'number' && Number.isFinite(desc.maxHeight))) {
      console.error('heightEncoding "normalizedCentered" requires finite maxHeight.');
      return null;
    }

    const maxHeight = desc.maxHeight ?? 1;
    const scaledHeights = new Float32Array(desc.heights.length);
    for (let i = 0; i < desc.heights.length; i++) {
      const inputValue = desc.heights[i];
      if (!Number.isFinite(inputValue)) {
        console.error(`Heightfield contains non-finite value at index ${i}.`);
        return null;
      }
      const worldHeight = encoding === 'normalizedCentered'
        ? (inputValue * maxHeight - maxHeight / 2)
        : inputValue;
      scaledHeights[i] = worldHeight * worldScale.y;
    }

    return this.doCreateHeightFieldBody({
      motionType: PhysicsMotionType.Static,
      options,
      worldPos,
      worldQuat,
      mass: 0,
      width: desc.width * worldScale.x,
      depth: desc.depth * worldScale.z,
      widthPixels: desc.widthPixels,
      depthPixels: desc.depthPixels,
      heights: scaledHeights,
      colliderRotationCorrection: desc.colliderRotationCorrection,
    });
  }

  /**
   * Creates a physics record for a TerrainMeshComponent.
   * @param component - The terrain mesh component
   * @param physicsOptions - The physics options
   * @param worldPos - The world position
   * @param worldQuat - The world quaternion
   * @param worldScale - The world scale
   * @returns [record, motionType] tuple
   */
  protected createTerrainMeshRecord(
    component: TerrainMeshComponent,
    physicsOptions: ComponentPhysicsOptions | undefined,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    worldScale: THREE.Vector3
  ): [TRecord | null, PhysicsMotionType | null] {
    const heightMap = component.getHeightMap();
    // Apply world scale to terrain dimensions
    const width = component.width * worldScale.x;
    const depth = component.depth * worldScale.z;
    const widthPixels = component.widthSegments + 1;
    const depthPixels = component.depthSegments + 1;
    const numPixels = widthPixels * depthPixels;

    if (!ensure(heightMap)) {
      return [null, null];
    } else if (!ensure(heightMap!.length === numPixels)) {
      return [null, null];
    }

    // Scale the height values
    const scaledHeightMap = new Float32Array(heightMap!.length);
    for (let i = 0; i < heightMap!.length; i++) {
      scaledHeightMap[i] = heightMap![i] * worldScale.y;
    }

    const motionType = PhysicsMotionType.Static;
    const record = this.doCreateHeightFieldBody({
      motionType,
      options: physicsOptions!,
      worldPos,
      worldQuat,
      mass: 0,
      width,
      depth,
      widthPixels,
      depthPixels,
      heights: scaledHeightMap,
    });

    return [record, motionType];
  }

  /**
   * Creates a physics record for a ModelMeshComponent.
   * @param component - The model mesh component
   * @param physicsOptions - The physics options
   * @param worldPos - The world position
   * @param worldQuat - The world quaternion
   * @param worldScale - The world scale
   * @returns [record, motionType] tuple
   */
  protected createGLTFMeshRecord(
    component: ModelMeshComponent,
    physicsOptions: ComponentPhysicsOptions,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    worldScale: THREE.Vector3,
  ): [TRecord | null, PhysicsMotionType | null] {
    // Only proceed if the model is loaded
    if (!component.isModelLoaded()) {
      return [null, null];
    }

    const motionType = physicsOptions?.motionType ?? PhysicsMotionType.Dynamic;

    const model = component.getModelTemplate();  // use model template as that's the copy living in resource manager and making it easy for cache
    let record: TRecord | null = null;
    const isVoxelsCollision = physicsOptions?.collisionMeshType === CollisionMeshType.Voxels;
    if (isVoxelsCollision) {
      const voxelsCollisionDesc: VoxelCollisionDesc | undefined = physicsOptions.collisionData?.voxels;
      if (!voxelsCollisionDesc) {
        console.log('No voxel collision data provided, using default single voxel at origin. Either provide collisionData.voxels in constructor, or use `overridePhysicsOptions`/`replacePhysicsOptions` to set it later.');
      }
      const voxelSize = voxelsCollisionDesc?.voxelSize ?? new THREE.Vector3(1, 1, 1);
      const voxels: Float32Array | Int32Array = voxelsCollisionDesc?.coordinates ?? new Int32Array([0, 0, 0]);
      const voxelCount = voxels.length / 3;
      const mass = this.calculateBoxMass(voxelSize, physicsOptions?.density) * voxelCount;
      record = this.doCreateVoxelBody({
        motionType,
        options: physicsOptions!,
        worldPos,
        worldQuat,
        mass,
        voxels,
        voxelSize,
        offset: voxelsCollisionDesc?.offset,
      });
    }
    else {
    // Collect all meshes from the model and create a single combined physics body
      if (model) {
      // don't bother finding physics meshes for voxel collision, as it comes from the physicsOptions.collisionData provided by user
        const physicsMeshes = this.processGLTFModelForPhysics(model);

        // Create a single physics body for all meshes combined
        if (physicsMeshes.length > 0) {
          const mass = physicsMeshes.reduce((sum, mesh) => sum + this.calculateMeshMass(mesh.geometry, physicsOptions?.density, mesh.scale), 0);

          record = this.doCreateMeshBody({
            motionType: motionType!,
            options: physicsOptions!,
            worldPos,
            worldQuat,
            mass,
            meshes: physicsMeshes,
          }, worldScale);
        }
      }
    }

    return [record, motionType];
  }

  /**
   * @param component - The instanced mesh component
   * @param physicsOptions - The physics options
   * @param worldPos - The world position
   * @param worldQuat - The world quaternion
   * @param worldScale - The world scale
   * @returns [records, motionType] tuple
   */
  protected createInstancedMeshRecord(
    component: InstancedMeshComponent,
    physicsOptions: ComponentPhysicsOptions | undefined,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion,
    worldScale: THREE.Vector3
  ): [TRecord[], PhysicsMotionType | null] {
    let records: TRecord[] = [];
    const motionType = physicsOptions?.motionType ?? PhysicsMotionType.Dynamic;
    const transforms = component.getInstancesTransforms(true);
    const isVoxelsCollision = physicsOptions?.collisionMeshType === CollisionMeshType.Voxels;
    const mesh = component.getInstancedMesh();
    if (!mesh) {
      return [[], null];
    }

    const instanceCount = component.getInstanceCount();
    if (!instanceCount || instanceCount === 0) {
      return [[], null];
    }

    const worldTransforms = isVoxelsCollision ? [{worldPos, worldQuat, worldScale}] :
      transforms.map(transform => ({
        worldPos: transform.position ?? new THREE.Vector3(),
        worldQuat: transform.rotation ? new THREE.Quaternion().setFromEuler(transform.rotation) : new THREE.Quaternion(),
        worldScale: transform.scale ?? new THREE.Vector3(1, 1, 1)
      }));

    const [createdRecords, ] = this.createMeshRecord(mesh, physicsOptions, worldTransforms, component);
    records = createdRecords.filter(r => r !== null) as TRecord[];

    return [records, motionType];
  }

  /**
   * Creates physics records for an InstancedModelMeshComponent.
   * @param component - The instanced GLTF mesh component
   * @param physicsOptions - The physics options
   * @param worldPos - The world position
   * @param worldQuat - The world quaternion
   * @returns [records, motionType] tuple
   */
  protected createInstancedGltfMeshRecord(
    component: InstancedModelMeshComponent,
    physicsOptions: ComponentPhysicsOptions | undefined,
    worldPos: THREE.Vector3,
    worldQuat: THREE.Quaternion
  ): [TRecord[], PhysicsMotionType | null] {
    // Only proceed if the model is loaded (check if we have instanced meshes)
    if (component.isLoading()) {
      return [[], null];
    }

    const instancedMeshes = component.getInstancedMeshes();
    if (!instancedMeshes || instancedMeshes.length === 0) {
      return [[], null];
    }

    // Get the number of instances
    const instanceCount = component.getInstanceCount();

    if (!instanceCount || instanceCount === 0) {
      return [[], null];
    }

    let records: TRecord[] = [];
    const motionType = physicsOptions?.motionType ?? PhysicsMotionType.Dynamic;
    const isVoxelsCollision = physicsOptions?.collisionMeshType === CollisionMeshType.Voxels;
    if (isVoxelsCollision) {
      const voxelsCollisionDesc: VoxelCollisionDesc | undefined = physicsOptions.collisionData?.voxels;
      if (!voxelsCollisionDesc) {
        console.log('No voxel collision data provided, using default single voxel at origin. Either provide collisionData.voxels in constructor, or use `overridePhysicsOptions`/`replacePhysicsOptions` to set it later.');
      }
      const voxelSize = voxelsCollisionDesc?.voxelSize ?? new THREE.Vector3(1, 1, 1);
      const voxels: Float32Array | Int32Array = voxelsCollisionDesc?.coordinates ?? new Int32Array([0, 0, 0]);
      const voxelCount = voxels.length / 3;
      const mass = this.calculateBoxMass(voxelSize, physicsOptions?.density) * voxelCount;
      const record = this.doCreateVoxelBody({
        motionType,
        options: physicsOptions!,
        worldPos,
        worldQuat,
        mass,
        voxels,
        voxelSize,
        offset: voxelsCollisionDesc?.offset,
      });
      if (record) {
        records = [record];
      }
    }
    else {
      const model = component.getModelTemplate();
      if (model) {
      // Process the GLTF model to create physics meshes (same as regular GLTF)
        const physicsMeshes = this.processGLTFModelForPhysics(model);

        // Create physics bodies for the instances
        if (physicsMeshes.length > 0) {
          const transforms = component.getInstancesTransforms(true);
          const paramTransforms = transforms.map(transform => ({
            position: transform.position ?? new THREE.Vector3(),
            rotation: transform.rotation ? new THREE.Quaternion().setFromEuler(transform.rotation) : new THREE.Quaternion(),
            scale: transform.scale ?? new THREE.Vector3(1, 1, 1)
          }));

          records = this.doCreateMeshBodiesWithTransformOverrides({
            motionType: motionType!,
            options: physicsOptions!,
            worldPos: paramTransforms[0].position,
            worldQuat: paramTransforms[0].rotation,
            // sum of all physicsMeshes
            mass: physicsMeshes.reduce((sum, mesh) => sum + this.calculateMeshMass(mesh.geometry, physicsOptions?.density), 0),
            meshes: physicsMeshes,
          }, paramTransforms);
        }
      }
    }

    return [records, motionType];
  }

  /**
     * Removes a scene component from the physics simulation.
     * @param component - The scene component to remove
     */
  public removeComponent(component: PrimitiveComponent): void {
    if (!ensure(this.initialized)) {
      return;
    }

    const records = this.bodies.get(component);
    if (!records) {
      return;
    }

    records.forEach((record) => {
      this.doRemoveComponent(record);
    });
    this.bodies.delete(component);
  }

  /**
     * Updates the transform (position/rotation) of a physics body.
     * @param component - The primitive component whose transform should be updated
     */
  public updateTransform(component: PrimitiveComponent): void {
    if (!ensure(this.initialized)) {
      return;
    }

    const records = this.bodies.get(component);
    if (!records) {
      return;
    }

    records.forEach((record) => {
      this.doUpdateTransform(component, record);
    });
  }

  /**
     * Gets a scalar physics parameter value for a component.
     * @param component - The scene component to query
     * @param param - The scalar parameter to get
     * @returns The parameter value or null if not found
     */
  public getScalarParam(component: PrimitiveComponent, param: PhysicsScalarParam): number | null {
    if (!ensure(this.initialized)) {
      return null;
    }

    const records = this.bodies.get(component);
    if (!records || records.length === 0) {
      return null;
    }

    return this.doGetScalarParam(records[0], param);
  }

  /**
     * Sets a scalar physics parameter value for a component.
     * @param component - The scene component to modify
     * @param param - The scalar parameter to set
     * @param value - The new parameter value
     * @returns True if the parameter was set successfully
     */
  public setScalarParam(component: PrimitiveComponent, param: PhysicsScalarParam, value: number): boolean {
    if (!ensure(this.initialized)) {
      return false;
    }

    const records = this.bodies.get(component);
    if (!records) {
      return false;
    }

    return this.doSetScalarParam(records, param, value);
  }

  /**
     * Gets a vector physics parameter value for a component.
     * @param component - The scene component to query
     * @param param - The vector parameter to get
     * @returns The parameter value array or null if not found
     */
  public getVectorParam(component: PrimitiveComponent, param: PhysicsVectorParam): VectorArray | null {
    if (!ensure(this.initialized)) {
      return null;
    }

    const records = this.bodies.get(component);
    if (!records || records.length === 0) {
      return null;
    }

    return this.doGetVectorParam(records[0], param);
  }

  /**
     * Sets a vector physics parameter value for a component.
     * @param component - The scene component to modify
     * @param param - The vector parameter to set
     * @param value - The new parameter value array
     * @returns True if the parameter was set successfully
     */
  public setVectorParam(component: PrimitiveComponent, param: PhysicsVectorParam, value: VectorArray): boolean {
    if (!ensure(this.initialized)) {
      return false;
    }

    const records = this.bodies.get(component);
    if (!records || records.length === 0) {
      return false;
    }
    return records.reduce((acc, record) => acc && this.doSetVectorParam(record, param, value), true);
  }

  /**
   * Clears a vector physics parameter value for a component.
   * @param component - The scene component to modify
   * @param param - The vector parameter to clear
   * @returns True if the parameter was cleared successfully
   */
  public clearVectorParam(component: PrimitiveComponent, param: PhysicsVectorParam): boolean {
    if (!ensure(this.initialized)) {
      return false;
    }

    const records = this.bodies.get(component);
    if (!records || records.length === 0) {
      return false;
    }
    return records.reduce((acc, record) => acc && this.doClearVectorParam(record, param), true);
  }

  /**
   * Create a character controller for a scene component.
   * @param movementComponent - Component to create controller for
   * @param options - Character controller options
   * @returns True if the character controller was created successfully, false otherwise
   */
  public createCharacterController(movementComponent: BasePawnMovementComponent, options: CharacterControllerOptions): boolean {
    if (!ensure(this.initialized)) {
      return false;
    }

    if (!ensure(!this.characterControllers.has(movementComponent))) {
      return false;
    }

    const record = this.doCreateCharacterController(options);
    if (!record) {
      return false;
    }
    this.characterControllers.set(movementComponent, record);
    return true;
  }

  /**
   * Remove a character controller for a scene component.
   * @param movementComponent - Component to remove controller for
   */
  public removeCharacterController(movementComponent: BasePawnMovementComponent): void {
    if (!ensure(this.initialized)) {
      return;
    }

    const record = this.characterControllers.get(movementComponent);
    if (!record) {
      return;
    }

    if (this.doRemoveCharacterController(record)) {
      this.characterControllers.delete(movementComponent);
    }
  }

  /**
   * Compute character movement for a scene component.
   * @param movementComponent - Component to compute movement for
   * @param componentToMove - Component to move
   * @param movement - Movement vector [x, y, z]
   * @param ignoreCollision - Whether to ignore collisions
   * @returns Object containing isGrounded state
   */
  public computeCharacterMovement(movementComponent: BasePawnMovementComponent, componentToMove: PrimitiveComponent, movement: VectorArray, ignoreCollision: boolean, immediateBodySync: boolean = false): { isGrounded: boolean; actualMovement: THREE.Vector3 } {
    if (!ensure(this.initialized)) {
      return { isGrounded: false, actualMovement: new THREE.Vector3() };
    }

    const record = this.characterControllers.get(movementComponent);
    if (!record) {
      return { isGrounded: false, actualMovement: new THREE.Vector3() };
    }

    const recordsToMove = this.bodies.get(componentToMove);
    if (!recordsToMove || recordsToMove.length === 0) {
      return { isGrounded: false, actualMovement: new THREE.Vector3() };
    }

    // only handle recordsToMove[0] for now
    return this.doComputeCharacterMovement(record, recordsToMove[0], movement, ignoreCollision, immediateBodySync);
  }

  public teleportBody(component: PrimitiveComponent, position: THREE.Vector3): void {
    if (!ensure(this.initialized)) return;
    const records = this.bodies.get(component);
    if (!records || records.length === 0) return;
    this.doTeleportBody(records[0], position);
  }

  /**
   * Configure collision filtering between two components.
   * @param componentA - First component
   * @param componentB - Second component
   * @param ignore - Whether componentA should ignore collisions with componentB
   */
  public setComponentCollisionIgnore(componentA: PrimitiveComponent, componentB: PrimitiveComponent, ignore: boolean): boolean {
    if (!ensure(this.initialized)) {
      return false;
    }

    const recordsA = this.bodies.get(componentA);
    const recordsB = this.bodies.get(componentB);

    if (!recordsA || !recordsB) {
      return false;
    }

    let result = true;
    for (const recordA of recordsA) {
      for (const recordB of recordsB) {
        result = result && this.doSetComponentCollisionIgnore(recordA, recordB, ignore);
      }
    }

    return result;
  }

  /**
   * Creates a joint between two physics bodies.
   * @param desc - Joint description containing type, components, and connection points
   * @returns Joint ID if successful, null if failed
   */
  public createJoint(desc: JointDesc): string | null {
    if (!ensure(this.initialized)) {
      return null;
    }

    const recordsA = this.bodies.get(desc.componentA);
    const recordsB = this.bodies.get(desc.componentB);

    if (!recordsA || !recordsB) {
      console.error('Cannot create joint: one or both components not found in physics engine');
      return null;
    }

    if (recordsA.length !== 1 || recordsB.length !== 1) {
      console.error('Cannot create joint: one or both components have multiple or none body count');
      return null;
    }

    const jointRecord = this.doCreateJoint(desc, recordsA[0], recordsB[0]);
    if (!jointRecord) {
      return null;
    }

    const jointId = `joint_${this.nextJointId++}`;
    this.joints.set(jointId, jointRecord);
    return jointId;
  }

  /**
   * Removes a joint from the physics simulation.
   * @param jointId - The joint ID returned from createJoint
   * @returns True if the joint was successfully removed
   */
  public removeJoint(jointId: string): boolean {
    if (!ensure(this.initialized)) {
      return false;
    }

    const jointRecord = this.joints.get(jointId);
    if (!jointRecord) {
      return false;
    }

    if (this.doRemoveJoint(jointRecord)) {
      this.joints.delete(jointId);
      return true;
    }
    return false;
  }

  /**
   * Gets all active joint IDs.
   * @returns Array of joint IDs
   */
  public getActiveJoints(): string[] {
    return Array.from(this.joints.keys());
  }

  /**
   * Creates a joint directly from physics body records.
   * @param type - Joint type
   * @param recordA - First physics body record
   * @param recordB - Second physics body record
   * @param anchorA - Anchor point on first body
   * @param anchorB - Anchor point on second body
   * @param axis - Optional axis for revolute/prismatic joints
   * @returns Joint ID if successful, null if failed
   */
  public createJointFromRecords(
    type: JointType,
    recordA: TRecord,
    recordB: TRecord,
    anchorA: THREE.Vector3,
    anchorB: THREE.Vector3,
    axis?: THREE.Vector3
  ): string | null {
    if (!ensure(this.initialized)) {
      return null;
    }

    const jointRecord = this.doCreateJointFromRecords(type, recordA, recordB, anchorA, anchorB, axis);
    if (!jointRecord) {
      return null;
    }

    const jointId = `joint_${this.nextJointId++}`;
    this.joints.set(jointId, jointRecord);
    return jointId;
  }

  // Direct physics body management methods

  public createPhysicsBody(desc: PhysicsBodyDesc): string | null {
    if (!ensure(this.initialized)) {
      return null;
    }

    const record = this.doCreateDirectBody(desc);
    if (!record) {
      return null;
    }

    const handle = `body_${this.nextBodyId++}`;
    this.directBodies.set(handle, record);
    return handle;
  }

  public removePhysicsBody(handle: string): boolean {
    if (!ensure(this.initialized)) {
      return false;
    }

    const record = this.directBodies.get(handle);
    if (!record) {
      return false;
    }

    const success = this.doRemoveDirectBody(record);
    if (success) {
      this.directBodies.delete(handle);
    }
    return success;
  }

  public isValidPhysicsBody(handle: string): boolean {
    return this.directBodies.has(handle);
  }

  public getPhysicsBodyPosition(handle: string): THREE.Vector3 | null {
    const record = this.directBodies.get(handle);
    if (!record) {
      return null;
    }
    return this.doGetBodyPosition(record);
  }

  public getPhysicsBodyRotation(handle: string): THREE.Quaternion | null {
    const record = this.directBodies.get(handle);
    if (!record) {
      return null;
    }
    return this.doGetBodyRotation(record);
  }

  public setPhysicsBodyPosition(handle: string, position: THREE.Vector3): boolean {
    const record = this.directBodies.get(handle);
    if (!record) {
      return false;
    }
    return this.doSetBodyPosition(record, position);
  }

  public setPhysicsBodyRotation(handle: string, rotation: THREE.Quaternion): boolean {
    const record = this.directBodies.get(handle);
    if (!record) {
      return false;
    }
    return this.doSetBodyRotation(record, rotation);
  }

  public applyImpulseToPhysicsBody(handle: string, impulse: THREE.Vector3, worldSpace: boolean = true): boolean {
    const record = this.directBodies.get(handle);
    if (!record) {
      return false;
    }
    return this.doApplyBodyImpulse(record, impulse, worldSpace);
  }

  public setPhysicsBodyEnabled(handle: string, enabled: boolean): boolean {
    const record = this.directBodies.get(handle);
    if (!record) {
      return false;
    }
    return this.doSetBodyEnabled(record, enabled);
  }

  public createJointFromBodies(
    type: JointType,
    bodyA: string,
    bodyB: string,
    anchorA: THREE.Vector3,
    anchorB: THREE.Vector3,
    axis?: THREE.Vector3
  ): string | null {
    if (!ensure(this.initialized)) {
      return null;
    }

    const recordA = this.directBodies.get(bodyA);
    const recordB = this.directBodies.get(bodyB);

    if (!recordA || !recordB) {
      return null;
    }

    const jointRecord = this.doCreateJointFromRecords(type, recordA, recordB, anchorA, anchorB, axis);
    if (!jointRecord) {
      return null;
    }

    const jointId = `joint_${this.nextJointId++}`;
    this.joints.set(jointId, jointRecord);
    return jointId;
  }

  //====================================================================================================

  protected calculateBoxMass(size: THREE.Vector3, density: number | undefined): number {
    const volume = size.x * size.y * size.z;
    return volume * (density ?? 1);
  }

  protected calculateSphereMass(radius: number, density: number | undefined): number {
    const volume = (4 / 3) * Math.PI * radius * radius * radius;
    return volume * (density ?? 1);
  }

  protected calculateCylinderMass(height: number, radius: number, density: number | undefined): number {
    const volume = Math.PI * radius * radius * height;
    return volume * (density ?? 1);
  }

  protected calculateCapsuleMass(height: number, radius: number, density: number | undefined): number {
    // Capsule is a cylinder with two hemisphere caps
    const cylinderVolume = Math.PI * radius * radius * height;
    const hemisphereVolume = (2/3) * Math.PI * radius * radius * radius;
    const volume = cylinderVolume + 2 * hemisphereVolume;
    return volume * (density ?? 1);
  }

  protected calculateConeMass(height: number, radius: number, density: number | undefined): number {
    const volume = (1 / 3) * Math.PI * radius * radius * height;
    return volume * (density ?? 1);
  }

  protected calculateMeshMass(geometry: THREE.BufferGeometry, density: number | undefined, scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1)): number {
    // Approximate mass based on bounding box volume
    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox;
    if (!bbox) {
      return 1; // default mass
    }
    const size = new THREE.Vector3();
    bbox.getSize(size);
    const volume = size.x * size.y * size.z * scale.x * scale.y * scale.z;
    return volume * (density ?? 1);
  }


    protected abstract doCreate(options: PhysicsOptions): Promise<boolean>;
    protected abstract doDestroy(): boolean;
    protected abstract doTick(deltaTime: number): void;
    protected abstract doRemoveComponent(record: TRecord): boolean;
    protected abstract doUpdateTransform(component: SceneComponent, record: TRecord): void;
    protected abstract doGetScalarParam(record: TRecord, param: PhysicsScalarParam): number | null;
    protected abstract doSetScalarParam(records: TRecord[], param: PhysicsScalarParam, value: number): boolean;
    protected abstract doGetVectorParam(record: TRecord, param: PhysicsVectorParam): VectorArray | null;
    protected abstract doSetVectorParam(record: TRecord, param: PhysicsVectorParam, value: VectorArray): boolean;
    protected abstract doClearVectorParam(record: TRecord, param: PhysicsVectorParam): boolean;
    protected abstract doCreateBoxBody(desc: BoxBodyDesc): TRecord | null;
    protected abstract doCreateMeshBody(desc: MeshBodyDesc, scale: THREE.Vector3): TRecord | null;
    protected abstract doCreateMeshBodiesWithTransformOverrides(desc: MeshBodyDesc, transforms: {position?: THREE.Vector3, rotation?: THREE.Quaternion, scale?: THREE.Vector3}[]): TRecord[];
    protected abstract doCreateVoxelBody(desc: VoxelBodyDesc): TRecord | null;
    protected abstract doCreateSphereBody(desc: SphereBodyDesc): TRecord | null;
    protected abstract doCreateCylinderBody(desc: CylinderBodyDesc): TRecord | null;
    protected abstract doCreateCapsuleBody(desc: CapsuleBodyDesc): TRecord | null;
    protected abstract doCreateConeBody(desc: ConeBodyDesc): TRecord | null;
    protected abstract doCreateHeightFieldBody(desc: HeightFieldBodyDesc): TRecord | null;
    protected doCreateCharacterController(options: CharacterControllerOptions): TCharacterControllerRecord | null {
      throw new Error('Not implemented');
    }
    protected doRemoveCharacterController(record: TCharacterControllerRecord): boolean {
      throw new Error('Not implemented');
    }
    protected doComputeCharacterMovement(record: TCharacterControllerRecord, recordToMove: TRecord, movement: VectorArray, ignoreCollision: boolean, immediateBodySync: boolean = false): { isGrounded: boolean; actualMovement: THREE.Vector3 } {
      throw new Error('Not implemented');
    }

    protected doTeleportBody(record: TRecord, position: THREE.Vector3): void {
      throw new Error('Not implemented');
    }
    protected doSetComponentCollisionIgnore(recordA: TRecord, recordB: TRecord, ignore: boolean): boolean {
      throw new Error('Not implemented');
    }
    protected abstract doCreateJoint(desc: JointDesc, recordA: TRecord, recordB: TRecord): TJointRecord | null;
    protected abstract doRemoveJoint(jointRecord: TJointRecord): boolean;
    protected abstract doCreateJointFromRecords(
      type: JointType,
      recordA: TRecord,
      recordB: TRecord,
      anchorA: THREE.Vector3,
      anchorB: THREE.Vector3,
      axis?: THREE.Vector3
    ): TJointRecord | null;

    // Abstract methods for direct body management
    protected abstract doCreateDirectBody(desc: PhysicsBodyDesc): TRecord | null;
    protected abstract doRemoveDirectBody(record: TRecord): boolean;
    protected abstract doGetBodyPosition(record: TRecord): THREE.Vector3 | null;
    protected abstract doGetBodyRotation(record: TRecord): THREE.Quaternion | null;
    protected abstract doSetBodyPosition(record: TRecord, position: THREE.Vector3): boolean;
    protected abstract doSetBodyRotation(record: TRecord, rotation: THREE.Quaternion): boolean;
    protected abstract doApplyBodyImpulse(record: TRecord, impulse: THREE.Vector3, worldSpace: boolean): boolean;
    protected abstract doSetBodyEnabled(record: TRecord, enabled: boolean): boolean;
    public abstract getComponentState(component: PrimitiveComponent): PrimitiveComponentPhysicsState | null;
    public abstract getCollisionEvents(): RawCollisionEvent[];
    public abstract clearCollisionEvents(): void;
    public abstract setDebugRendererEnabled(enabled: boolean): boolean;
    public abstract isDebugRendererEnabled(): boolean;

    /**
   * Process any pending collision events in the physics system
   * This is called internally by the tick method
   */
    @THREE.profile
    protected processCollisionEvents(): void {
      const events = this.getCollisionEvents();
      for (const event of events) {
        this.handleCollisionEvent(event);
      }
      // Clear the collision events after processing them
      this.clearCollisionEvents();
    }

    /**
   * Handles a single collision event by notifying the involved components.
   * @param event - Collision event information
   */
    protected handleCollisionEvent(event: RawCollisionEvent): void {
      const componentA = event.componentA;
      const componentB = event.componentB;

      const eventA: CollisionEvent = {
        thisComponent: componentA,
        otherComponent: componentB,
        thisPoint: event.pointA,
        thisNormal: event.normalA,
        otherPoint: event.pointB,
        otherNormal: event.normalB,
        relativeVelocity: event.relativeVelocity,
      };

      const eventB: CollisionEvent = {
        thisComponent: componentB,
        otherComponent: componentA,
        thisPoint: event.pointB,
        thisNormal: event.normalB,
        otherPoint: event.pointA,
        otherNormal: event.normalA,
        relativeVelocity: event.relativeVelocity,
      };

      if (componentA && componentB) {
        const actorA = componentA.getActor();
        const actorB = componentB.getActor();

        if(event.eventType === CollisionEventType.Started) {
          if (event.isTriggerEvent) {
          // Component events
            componentA.onOverlapWith.invoke(componentA, componentB, eventA);
            componentB.onOverlapWith.invoke(componentB, componentA, eventB);
            // Actor events
            if (actorA && actorB) actorA.onOverlapWith.invoke(actorA, componentA, actorB, componentB, eventA);
            if (actorB && actorA) actorB.onOverlapWith.invoke(actorB, componentB, actorA, componentA, eventB);
          } else {
          // Component events
            componentA.onCollideWith.invoke(componentA, componentB, eventA);
            componentB.onCollideWith.invoke(componentB, componentA, eventB);
            // Actor events
            if (actorA && actorB) actorA.onCollideWith.invoke(actorA, componentA, actorB, componentB, eventA);
            if (actorB && actorA) actorB.onCollideWith.invoke(actorB, componentB, actorA, componentA, eventB);
          }
        } else {
          if (event.isTriggerEvent) {
          // Component events
            componentA.onStopOverlappingWith.invoke(componentA, componentB, eventA);
            componentB.onStopOverlappingWith.invoke(componentB, componentA, eventB);
            // Actor events
            if (actorA && actorB) actorA.onStopOverlappingWith.invoke(actorA, componentA, actorB, componentB, eventA);
            if (actorB && actorA) actorB.onStopOverlappingWith.invoke(actorB, componentB, actorA, componentA, eventB);
          } else {
          // Component events
            componentA.onStopCollidingWith.invoke(componentA, componentB, eventA);
            componentB.onStopCollidingWith.invoke(componentB, componentA, eventB);
            // Actor events
            if (actorA && actorB) actorA.onStopCollidingWith.invoke(actorA, componentA, actorB, componentB, eventA);
            if (actorB && actorA) actorB.onStopCollidingWith.invoke(actorB, componentB, actorA, componentA, eventB);
          }
        }
      }
    }

    // Vehicle controller methods - default implementations that throw errors
    public abstract createVehicle(movementComponent: BasePawnMovementComponent, chassisComponent: PrimitiveComponent): IPhysicsVehicle | null;
}
